import { useEffect, useState } from "react";
import { Box, Button, Divider, Link as MuiLink, Stack, Typography, alpha } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import { SectionCard } from "@/components/common/SectionCard";
import { StatusChip, type StatusChipColor } from "@/components/common/fields/StatusChipField";
import { ClaimantPolicyNoteCell } from "@/components/common/ClaimantPolicyNoteCell";
import { ClaimsSourceChip } from "@/components/ui/mui/ClaimsSourceChip";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { ResourceName, StoplossClaimStatus, type StoplossClaim } from "@/types";
import { UIComponentSize } from "@/types/shared/uiComponents";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatDate } from "@/utils/formatters/dateFormatter";
import { deductibleWord } from "@/resources/stoploss-claims/components/rail/claimAmountCopy";
import { auraTrackState, carrierTrackChip } from "@/resources/stoploss-claims/twoTrackState";
import { useClaimantPage, useClaimantSpend } from "../../hooks/useClaimantPage";
import { getExcludedByBasis, getResolvedASDValues } from "../../hooks/useCurrentPolicyData";
import { ClaimSource, getClaimsSource } from "../../utils/claimsSource";
import { DeductibleReviewBanner } from "../deductible-review/DeductibleReviewBanner";
import { PendingFamilyUnitBanner } from "../PendingFamilyUnitBanner";
import { SuspectedDuplicateBanner } from "../SuspectedDuplicateBanner";

const LABEL_SX = { fontSize: "0.875rem", fontWeight: 600, color: "text.secondary" } as const;
const CAPTION_SX = { fontSize: "0.8125rem", color: "text.secondary" } as const;
const quietLink = { color: "inherit", "&:hover": { color: "primary.main" } } as const;
const whole = (n: number) => formatUSD(n, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const ageOf = (birth?: string): number | undefined => {
  if (!birth) return undefined;
  const d = new Date(birth.slice(0, 10));
  if (Number.isNaN(d.getTime())) return undefined;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age;
};

/**
 * The most actionable state across the claimant's claims, as one chip on the
 * claims line: a hold first, then a claim with funding not yet requested on an
 * Accel policy, then one not yet submitted to the carrier; otherwise nothing.
 */
export const claimsLineChip = (
  claims: StoplossClaim[],
  accelEnabled: boolean,
  held: boolean
): { label: string; color: StatusChipColor } | null => {
  if (claims.length === 0) return null;
  if (held) return { label: "Held", color: "warning" };
  const open = claims.filter((c) => c.claim_status !== StoplossClaimStatus.DENIED);
  const notRequested = accelEnabled ? open.filter((c) => auraTrackState(c) === "not_requested").length : 0;
  if (notRequested > 0) return { label: `${notRequested} not yet requested`, color: "default" };
  const notSubmitted = open.filter((c) => !carrierTrackChip(c)).length;
  if (notSubmitted > 0) return { label: `${notSubmitted} not yet submitted`, color: "default" };
  return null;
};

/** The hold strips, in the card's first slot. Both can show at once; each explains itself. */
export const ClaimantHoldStrips = ({ onManageFamily }: { onManageFamily: () => void }) => {
  const { claimant } = useClaimantPage();
  // Always mounted: DeductibleReviewBanner owns the #4243 task deep link
  // (?review=open&policy=…), which must select the term and open the dialog even
  // when the selected term is not held (review !5050 [AEM-review-deeplink-gate]).
  // Each banner renders nothing when it has nothing to say; an empty stack hides.
  return (
    <Stack spacing={1} data-testid="claimant-hold-strips" sx={{ "&:empty": { display: "none" } }}>
      <DeductibleReviewBanner variant="strip" />
      <PendingFamilyUnitBanner claimant={claimant} onFormFamilyUnit={onManageFamily} variant="strip" />
    </Stack>
  );
};

const IdentityLine = ({ onManageFamily }: { onManageFamily: () => void }) => {
  const { claimant, familyUnit } = useClaimantPage();
  const age = ageOf(claimant.claimant_birth_date);
  const parts: React.ReactNode[] = [];
  if (claimant.claimant_birth_date)
    parts.push(
      <span key="dob">
        Born {formatDate(claimant.claimant_birth_date.slice(0, 10))}
        {age !== undefined ? ` (${age})` : ""}
      </span>
    );
  if (claimant.member_id) parts.push(<span key="mid">Medical ID {claimant.member_id}</span>);
  if (claimant.rx_member_id) parts.push(<span key="rx">PBM ID {claimant.rx_member_id}</span>);
  if (claimant.subscriber_ref_id) {
    const n = familyUnit.data?.members?.length;
    parts.push(
      <MuiLink
        key="fam"
        component="button"
        type="button"
        onClick={onManageFamily}
        underline="hover"
        sx={{ ...CAPTION_SX, ...quietLink, fontSize: "inherit", verticalAlign: "baseline" }}
        data-testid="family-link"
      >
        {n ? `Family of ${n}` : "Family unit"}
      </MuiLink>
    );
  }
  if (parts.length === 0) return null;
  return (
    <Box
      data-testid="identity-line"
      sx={{
        ...CAPTION_SX,
        display: "flex",
        flexWrap: "wrap",
        gap: "2px 12px",
        pb: 1,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      {parts}
    </Box>
  );
};

/**
 * "$X over the $D lasered deductible" / "$X to the $D deductible", the word
 * linking to the Deductible tab. Three shapes (claimant-authority review
 * 2026-10-27): the individual deductible; the family pool on a family-deductible
 * policy (the individual deductible sizes nothing there); and a contract laser,
 * whose spend is windowed and not on the wire, so only an engine-computed
 * overage is shown and the bar is omitted when there is none.
 */
const PositionLine = () => {
  const { claimantId, effectiveDeductible, isASD, familyLayout, familyUnit } = useClaimantPage();
  const spend = useClaimantSpend();
  const total = spend?.total_paid ?? 0;
  const { amount, source } = effectiveDeductible;
  const word = deductibleWord(source);
  const tabHref = `/claimants/${claimantId}/show/deductibles`;
  const link = (text: string) => (
    <MuiLink component={RouterLink} to={tabHref} underline="hover" sx={quietLink}>
      {text}
    </MuiLink>
  );
  if (source === "EXCLUDED") {
    return (
      <Typography sx={CAPTION_SX} data-testid="position-line">
        {link("Excluded")} from the carrier contract: no specific deductible this term.
      </Typography>
    );
  }

  // Family pool: the sentence and the bar are the family's; the member's spend is their share.
  const fam = familyLayout ? familyUnit.data : null;
  if (fam && fam.familyDeductible && fam.familyDeductible > 0) {
    const famOver = Math.max(fam.familyTotalSpend - fam.familyDeductible, 0);
    const famToGo = Math.max(fam.familyDeductible - fam.familyTotalSpend, 0);
    const famPct = Math.min((fam.familyTotalSpend / fam.familyDeductible) * 100, 100);
    return (
      <Box data-testid="position-line" data-shape="family">
        <Bar pct={famPct} />
        <Typography sx={{ fontSize: "0.8125rem", fontVariantNumeric: "tabular-nums" }}>
          Family {whole(fam.familyTotalSpend)}:{" "}
          {famOver > 0 ? (
            <>
              <b>{formatUSD(famOver)}</b> over the {whole(fam.familyDeductible)} {link("family deductible")}
            </>
          ) : (
            <>
              <b>{formatUSD(famToGo)}</b> to the {whole(fam.familyDeductible)} {link("family deductible")}
            </>
          )}
        </Typography>
        <Typography sx={{ ...CAPTION_SX, mt: 0.5 }} data-testid="family-share">
          Their share {formatUSD(total)}
          {fam.calculationHeld ? " · calculation held" : ""}. Claims are sized on the family pool.
        </Typography>
      </Box>
    );
  }

  if (!(amount > 0)) return null;
  // One basis for the bar, "to go" and "over": total_paid against the applied
  // deductible, the engine rule for every non-contract shape. Server
  // spend_over_deductible is kept only for the contract shape, whose windowed
  // total is not on the wire. A stale reported SOD otherwise read "$0 to go"
  // under a full bar (review !5050, expert medium 1).
  const over = source === "CONTRACT" ? (spend?.spend_over_deductible ?? 0) : Math.max(total - amount, 0);

  // Contract laser: only in-window spend counts and that figure is not on the wire,
  // so "to go" and the bar would be built from the wrong total.
  if (source === "CONTRACT") {
    return (
      <Box data-testid="position-line" data-shape="contract">
        <Typography sx={{ fontSize: "0.8125rem", fontVariantNumeric: "tabular-nums" }}>
          {over > 0 ? (
            <>
              <b>{formatUSD(over)}</b> over the {whole(amount)} {link("contract")} deductible
            </>
          ) : (
            <>
              Measured inside the contract windows against the {whole(amount)} {link("contract")} deductible
            </>
          )}
        </Typography>
        {isASD ? <AsdLines over={over} /> : null}
      </Box>
    );
  }

  const toGo = Math.max(amount - total, 0);
  const pct = Math.min((total / amount) * 100, 100);
  const sentence =
    over > 0 ? (
      <>
        <b>{formatUSD(over)}</b> over the {whole(amount)} {word ? link(word) : link("deductible")}
        {word ? " deductible" : ""}
      </>
    ) : (
      <>
        <b>{formatUSD(toGo)}</b> to the {whole(amount)} {word ? link(word) : link("deductible")}
        {word ? " deductible" : ""}
      </>
    );
  return (
    <Box data-testid="position-line">
      <Bar pct={pct} />
      <Typography sx={{ fontSize: "0.8125rem", fontVariantNumeric: "tabular-nums" }}>{sentence}</Typography>
      {isASD ? <AsdLines over={over} /> : null}
    </Box>
  );
};

const FILL_MS = 700;

/** One pass of light across the full bar once the fill has landed on a breach. */
const sweep = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(100%); }
`;

/**
 * Deductible position bar. The fill eases from empty to its value on mount, and
 * when spend has crossed the deductible a single highlight sweeps the full bar
 * after the fill lands (Dillon, 2026-10-28). Both honour prefers-reduced-motion.
 * The accessible value is the real percentage from the first render.
 */
const Bar = ({ pct }: { pct: number }) => {
  const breached = pct >= 100;
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setWidth(pct));
    return () => cancelAnimationFrame(frame);
  }, [pct]);
  return (
    <Box
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`${Math.round(pct)}% of the deductible`}
      data-breached={breached ? "true" : undefined}
      sx={{ position: "relative", height: 6, borderRadius: 3, bgcolor: "action.hover", my: 1, overflow: "hidden" }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          width: `${width}%`,
          bgcolor: "primary.main",
          borderRadius: 3,
          transition: `width ${FILL_MS}ms cubic-bezier(0.2, 0.8, 0.2, 1)`,
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      />
      {breached ? (
        <Box
          aria-hidden
          sx={(t) => ({
            position: "absolute",
            inset: 0,
            background: `linear-gradient(90deg, transparent 0%, ${alpha(t.palette.common.white, 0.55)} 50%, transparent 100%)`,
            transform: "translateX(-100%)",
            animation: `${sweep} 900ms ease-out ${FILL_MS + 100}ms 1 both`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none", display: "none" },
          })}
        />
      ) : null}
    </Box>
  );
};

const AsdLines = ({ over }: { over: number }) => {
  const { policyData } = useClaimantPage();
  const asd = getResolvedASDValues(policyData);
  if (!asd) return null;
  const consumed = asd.amt_asd_consumed ?? 0;
  const payable = asd.amt_carrier_payable ?? 0;
  // The allocation is a projection of the latest ASD run; until it reruns it can
  // lag the spend over the deductible. Show the split only when it adds up.
  if (Math.abs(consumed + payable - over) > 0.5) return null;
  return (
    <Stack spacing={0.25} sx={{ mt: 0.5 }} data-testid="asd-lines">
      <Row label="Absorbed by the aggregate corridor" value={formatUSD(consumed)} />
      <Row label="Carrier payable" value={formatUSD(payable)} />
    </Stack>
  );
};

const Row = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
    <Typography sx={CAPTION_SX}>{label}</Typography>
    <Typography sx={{ fontSize: "0.8125rem", fontWeight: strong ? 700 : 500, fontVariantNumeric: "tabular-nums" }}>
      {value}
    </Typography>
  </Box>
);

const SpendBlock = () => {
  const { policyData } = useClaimantPage();
  const spend = useClaimantSpend();
  const source = getClaimsSource(policyData?.claims);
  const perType = policyData?.authority_by_type;
  const basis = getExcludedByBasis(policyData);
  // Any reported part (either type on a carve-out, or a bundled reported total)
  // is a lump the contract windows cannot be applied to (ADR-081 §5.3).
  const anyReported =
    perType?.medical.authority === "reported" ||
    perType?.rx.authority === "reported" ||
    (!perType && source === ClaimSource.REPORTED);
  const total = spend?.total_paid;
  return (
    <Box data-testid="spend-block">
      <Typography sx={LABEL_SX}>Spend this policy year</Typography>
      <Typography
        sx={{ fontSize: "1.75rem", fontWeight: 700, lineHeight: 1.15, mt: 0.25, fontVariantNumeric: "tabular-nums" }}
      >
        {total !== undefined ? formatUSD(total) : "—"}
      </Typography>
      <Box sx={{ ...CAPTION_SX, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75, mt: 0.5 }}>
        {perType ? (
          <ClaimsSourceChip perType={perType} showAmounts />
        ) : source !== ClaimSource.NONE ? (
          <ClaimsSourceChip
            source={source}
            asOf={spend?.paid_through_date}
            lastUpdated={policyData?.claims?.last_updated}
            sourceName={policyData?.claims?.source_name}
          />
        ) : (
          <span>No claims data on this policy yet</span>
        )}
        {spend?.paid_through_date ? <span>paid through {formatDate(spend.paid_through_date)}</span> : null}
      </Box>
      {/* What the deductible basis dropped from this term's lines (ADR-081 D5.5, #7338), so
          the spend reconciles with the claim-line list. The resolver returns undefined with
          the flag off or before a compute: then no line, not $0.00. */}
      {basis?.window !== undefined || basis?.notPaid !== undefined ? (
        <Stack spacing={0.25} sx={{ mt: 0.75 }} data-testid="basis-lines">
          {basis?.window !== undefined ? (
            <Row label="Excluded by contract window" value={formatUSD(basis.window)} />
          ) : null}
          {basis?.notPaid !== undefined ? <Row label="Not in paid status" value={formatUSD(basis.notPaid)} /> : null}
        </Stack>
      ) : null}
      {policyData?.is_contract_lasered && anyReported ? (
        <Typography sx={{ ...CAPTION_SX, mt: 0.75 }} data-testid="contract-provided-hint">
          Part of this figure is carrier-provided, so the contract windows cannot be applied to it; the carrier decides
          eligibility.
        </Typography>
      ) : null}
    </Box>
  );
};

const ClaimsLine = () => {
  const { claimantId, claims, claimsAmount, deniedAmount, claimsLoading, claimsError, accelEnabled, held } =
    useClaimantPage();
  const n = claims.length;
  const chip = claimsLineChip(claims, accelEnabled, held);
  return (
    <Box
      sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}
      data-testid="claims-line"
    >
      <Typography sx={{ fontSize: "0.8125rem" }}>
        {claimsError ? (
          <span>Couldn&apos;t load the stop-loss claims</span>
        ) : claimsLoading ? (
          <span>Loading stop-loss claims…</span>
        ) : n === 0 ? (
          <span>No stop-loss claims on this policy</span>
        ) : (
          <>
            <MuiLink
              component={RouterLink}
              to={`/claimants/${claimantId}/show/claims`}
              underline="hover"
              sx={quietLink}
            >
              {n} stop-loss claim{n === 1 ? "" : "s"}
            </MuiLink>
            {" · "}
            <Box component="span" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 500 }}>
              {formatUSD(claimsAmount)}
            </Box>
            {deniedAmount > 0 ? (
              <Box component="span" sx={{ ...CAPTION_SX, fontVariantNumeric: "tabular-nums" }}>
                {" "}
                · {formatUSD(deniedAmount)} denied
              </Box>
            ) : null}
          </>
        )}
      </Typography>
      {chip ? <StatusChip label={chip.label} color={chip.color} size={UIComponentSize.Small} /> : null}
    </Box>
  );
};

const MoneyLines = () => {
  const { fundingRequested, reimbursedTotal, reimbursementsLoading, accelEnabled } = useClaimantPage();
  // No "Outstanding" line: claims − paid reimbursements treats unbilled claims as
  // owed and never clears denials or adjustments. Finance owns that definition.
  return (
    <Stack spacing={0.5} data-testid="money-lines">
      {accelEnabled ? <Row label="Funding requested" value={formatUSD(fundingRequested)} /> : null}
      <Row label="Reimbursed so far" value={reimbursementsLoading ? "…" : formatUSD(reimbursedTotal)} />
    </Stack>
  );
};

const NoPolicy = ({ onLinkPolicy }: { onLinkPolicy?: () => void }) => (
  <Box data-testid="no-policy">
    <Typography sx={LABEL_SX}>Spend this policy year</Typography>
    <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, color: "text.secondary", lineHeight: 1.15 }}>—</Typography>
    <Typography sx={{ ...CAPTION_SX, mt: 0.5 }}>
      Not linked to a policy, so there is no deductible to measure against and no claims can be created.
    </Typography>
    {onLinkPolicy ? (
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        onClick={onLinkPolicy}
        sx={{ mt: 1, textTransform: "none" }}
      >
        Link a policy
      </Button>
    ) : null}
  </Box>
);

interface RailProps {
  onManageFamily: () => void;
  onLinkPolicy?: () => void;
}

/** The position card: holds, identity, spend against the deductible, the claims line, the money lines. */
export const ClaimantPositionCard = ({ onManageFamily, onLinkPolicy }: RailProps) => {
  const { policy } = useClaimantPage();
  return (
    <SectionCard compact noDivider>
      {/* useFlexGap: a hidden (empty) strip stack must not leave a 12 px margin above the identity line. */}
      <Stack spacing={1.5} useFlexGap data-testid="position-card">
        <ClaimantHoldStrips onManageFamily={onManageFamily} />
        <IdentityLine onManageFamily={onManageFamily} />
        {policy ? (
          <>
            <SpendBlock />
            <PositionLine />
            <Divider />
            <ClaimsLine />
            <Divider />
            <MoneyLines />
          </>
        ) : (
          <NoPolicy onLinkPolicy={onLinkPolicy} />
        )}
      </Stack>
    </SectionCard>
  );
};

/** Notes (the one hand edit) and the duplicate line: the rail's lower cards, also shown under the tabs when collapsed. */
export const ClaimantRailCards = () => {
  const { claimant, claimantId, policyId, policyData } = useClaimantPage();
  const { canEdit } = useResourcePermissions(ResourceName.CLAIMANTS);
  return (
    <>
      {policyId ? (
        <SectionCard compact title="Notes" subtitle="This policy · also on the Claims Summary">
          <ClaimantPolicyNoteCell
            claimantId={claimantId}
            policyId={String(policyId)}
            note={policyData?.note}
            canEdit={canEdit}
            block
          />
        </SectionCard>
      ) : null}
      <SuspectedDuplicateBanner claimantId={claimant.id ? String(claimant.id) : undefined} variant="quiet" />
    </>
  );
};

export const ClaimantRail = (props: RailProps) => (
  <>
    <ClaimantPositionCard {...props} />
    <ClaimantRailCards />
  </>
);

/** The rail's stand-in under 700 px: holds, spend, the position line, the claims line, outstanding. */
export const ClaimantSummaryBar = ({ onManageFamily, onLinkPolicy }: RailProps) => {
  const { policy, reimbursedTotal } = useClaimantPage();
  const spend = useClaimantSpend();
  return (
    <SectionCard compact noDivider>
      <Stack spacing={1} useFlexGap data-testid="summary-bar">
        <ClaimantHoldStrips onManageFamily={onManageFamily} />
        {policy ? (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
              <Typography sx={CAPTION_SX}>Spend this policy year</Typography>
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                {spend?.total_paid !== undefined ? formatUSD(spend.total_paid) : "—"}
              </Typography>
            </Box>
            <PositionLine />
            <ClaimsLine />
            <Row label="Reimbursed so far" value={formatUSD(reimbursedTotal)} />
          </>
        ) : (
          <NoPolicy onLinkPolicy={onLinkPolicy} />
        )}
      </Stack>
    </SectionCard>
  );
};
